import React, { FC, useEffect } from 'react';
import { useLocalization } from 'localization';
import { Container, Typography, Stack } from '@mui/material';
import { DocumentUnknown } from '@carbon/icons-react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import * as Sentry from '@sentry/react';

import Styled from '../styled/ErrorPageStyled';
import { useWhitelabelSettings } from 'modules/branches/hooks';
import { AuthApi, IDatahubCase, IUser } from 'api';

export interface ErrorPageProps {
  standalonePage?: boolean;
  contentChild?: React.JSX.Element;
  title?: string;
  logoLink?: string;
  doesCurrentUserHaveAccess?: boolean;
  user?: IUser;
  foundCase?: IDatahubCase;
  source?: 'new-order-for-case';
}

const ErrorPage: FC<ErrorPageProps> = ({
  standalonePage = false,
  contentChild,
  title,
  logoLink,
  doesCurrentUserHaveAccess,
  user,
  foundCase,
  source,
}) => {
  const { t } = useLocalization();
  const { data: whitelabel } = useWhitelabelSettings(AuthApi.isAuthenticated());

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      Sentry.captureEvent({
        message: `404 page displayed${source ? ` for ${source}` : ''}`,
        extra: {
          url: window.location.href,
          doesCurrentUserHaveAccess,
          user: user ? JSON.stringify(user) : undefined,
          foundCase: foundCase ? JSON.stringify(foundCase) : undefined,
        },
      });
    }
  }, []);

  const Logo = (
    <Link to={logoLink || '/'}>
      <Styled.Logo src={whitelabel?.logoUrl || '/logo.png'} />
    </Link>
  );

  const defaultBodyContent = (
    <Stack
      direction="column"
      alignItems="center"
      justifyContent="center"
      spacing={2}
      sx={{
        minHeight: standalonePage ? '100vh' : 'auto',
        textAlign: 'center',
        mt: standalonePage ? 0 : 8,
      }}
    >
      <Typography variant="h5">404: {t('common.messages.resourceNotFound')}</Typography>
      <DocumentUnknown size={32} />
    </Stack>
  );

  return (
    <>
      <Helmet>
        <title>{title || `404: ${t('common.messages.resourceNotFound')}`}</title>
      </Helmet>

      <Container maxWidth="lg">
        {standalonePage}
        {contentChild || defaultBodyContent}
      </Container>
    </>
  );
};

export default ErrorPage;
